import { buildCreateProposalPluginPayload } from './createProposalPluginAdapters';

export const createProposalSubmissionModes = {
  MOCK_REVIEW: 'mock-review',
  BACKEND: 'backend',
  ONCHAIN: 'onchain',
};

const governanceApiBase = import.meta.env.VITE_GOVERNANCE_API_URL || '/governance-api';
const createProposalEndpoint = `${governanceApiBase}/v2/proposals/create`;
const backendCreateProposalEnabled = import.meta.env.VITE_GOVERNANCE_CREATE_PROPOSAL_ENABLED === 'true';

function createReason(reasonCode, reasonSeverity, source, message) {
  return {
    reasonCode,
    reasonSeverity,
    source,
    message,
  };
}

function createLayerReason(reasonCode, reasonSeverity, source) {
  return createReason(reasonCode, reasonSeverity ?? 'constitutional', source, 'Observed Constitutional Layer reason code attached to this create-proposal request.');
}

function buildCreateProposalReasonCodes({ draft, walletAddress, plugin, constitutionalLayer }) {
  const reasons = [];

  if (!backendCreateProposalEnabled) {
    reasons.push(
      createReason(
        'CREATE_PROPOSAL_BACKEND_NOT_ENABLED',
        'info',
        'frontend submission boundary',
        'Create proposal is currently a local mock workflow. Backend submission is not enabled.',
      ),
    );
  }

  if (!walletAddress) {
    reasons.push(createReason('WALLET_NOT_CONNECTED', 'warning', 'wallet or DAO permission state', 'A connected wallet is required before live proposal submission.'));
  }

  if (!plugin) {
    reasons.push(
      createReason(
        'PLUGIN_CAPABILITY_NOT_REGISTERED',
        'warning',
        'plugin capability',
        'No indexed plugin capability was selected for this proposal draft.',
      ),
    );
  }

  if (draft.actionType === 'treasury-review') {
    reasons.push(
      createReason(
        'TREASURY_POLICY_REQUIRES_REVIEW',
        'constitutional',
        'treasury policy',
        'Treasury-sensitive proposal drafts require backend and governance policy review before execution.',
      ),
    );
  }

  const layerReasons = [
    ...(constitutionalLayer?.executionModel?.reasonCodes ?? []).map((reasonCode) =>
      createLayerReason(reasonCode, constitutionalLayer?.executionModel?.reasonSeverity, 'constitutional execution model'),
    ),
    ...(constitutionalLayer?.conditions ?? []).flatMap((condition) =>
      (condition.reasonCodes ?? []).map((reasonCode) => createLayerReason(reasonCode, condition.reasonSeverity, condition.source ?? 'Constitutional Governance')),
    ),
  ];

  return Array.from(
    new Map([...reasons, ...layerReasons].map((reason) => [reason.reasonCode, reason])).values(),
  );
}

function resolvePluginType(plugin, draft) {
  return plugin?.interfaceType ?? plugin?.pluginType ?? draft?.pluginId ?? null;
}

function buildObservedGovernanceContext({ draft, selectedDao, selectedChain, plugin, pluginPayload, constitutionalLayer }) {
  const pluginType = resolvePluginType(plugin, draft);
  const chainPluginCapability = pluginType ? selectedChain?.capabilities?.pluginCapabilities?.[pluginType] : null;
  const treasuryReviewRequired =
    draft.actionType === 'treasury-review' ||
    pluginPayload.adapterFamily === 'treasury-policy' ||
    constitutionalLayer?.executionModel?.treasuryReviewRequired === true;

  const sources = {
    registry: {
      source: 'chain registry',
      observed: Boolean(selectedChain),
      network: selectedChain?.network ?? selectedChain?.slug ?? selectedDao?.network ?? null,
      governanceStatus: selectedChain?.governanceStatus ?? null,
      federationMember: selectedChain?.federationMember ?? null,
      federationTier: selectedChain?.federationTier ?? null,
      constitutionalStanding: selectedChain?.constitutionalStanding ?? selectedChain?.capabilities?.constitutionalStanding ?? null,
      constitutionalLayerAvailable: Boolean(constitutionalLayer),
    },
    dao: {
      source: 'DAO registry',
      observed: Boolean(selectedDao),
      id: selectedDao?.id ?? null,
      address: selectedDao?.address ?? null,
      name: selectedDao?.name ?? null,
      governanceStatus: selectedDao?.governanceStatus ?? null,
      federationMember: selectedDao?.federationMember ?? null,
      federationTier: selectedDao?.federationTier ?? null,
      constitutionalStanding: selectedDao?.constitutionalStanding ?? selectedDao?.constitutionalCompliance ?? selectedDao?.constitutionalCompatibility ?? null,
    },
    plugin: {
      source: chainPluginCapability ? 'chain plugin capability registry' : 'observed plugin selection',
      observed: Boolean(plugin),
      id: plugin?.id ?? draft.pluginId ?? null,
      address: plugin?.address ?? null,
      interfaceType: pluginType,
      label: plugin?.name ?? plugin?.label ?? draft.pluginLabel ?? pluginType ?? null,
      capability: chainPluginCapability,
      createProposalAdapter: {
        family: pluginPayload.adapterFamily,
        status: pluginPayload.adapterStatus,
        expectedBackendAdapter: pluginPayload.expectedBackendAdapter,
        executionIntent: pluginPayload.executionIntent,
      },
    },
    treasuryPolicy: {
      source: 'treasury policy',
      observed: treasuryReviewRequired,
      reviewRequired: treasuryReviewRequired,
      actionType: draft.actionType,
      reasonCodes: treasuryReviewRequired ? ['TREASURY_POLICY_REQUIRES_REVIEW'] : [],
      reasonSeverity: treasuryReviewRequired ? 'constitutional' : null,
      boundary: 'Treasury policy visibility is request metadata only. Backend policy engines and governance sources must determine final restrictions.',
    },
  };

  return {
    ...sources,
    observedSources: Object.entries(sources)
      .filter(([, value]) => value.observed)
      .map(([key, value]) => ({
        key,
        source: value.source,
        status: key === 'treasuryPolicy' ? (value.reviewRequired ? 'requires-review' : 'not-required') : 'observed',
      })),
  };
}

export function buildCreateProposalRequest({
  draft,
  selectedDao,
  selectedChain,
  walletAddress,
  plugin,
  submissionMode = backendCreateProposalEnabled ? createProposalSubmissionModes.BACKEND : createProposalSubmissionModes.MOCK_REVIEW,
}) {
  const constitutionalLayer = selectedChain?.constitutionalLayer ?? selectedChain?.capabilities?.constitutionalLayer ?? null;
  const pluginPayload = buildCreateProposalPluginPayload({ draft, selectedDao, selectedChain, walletAddress, plugin });
  const observedGovernanceContext = buildObservedGovernanceContext({
    draft,
    selectedDao,
    selectedChain,
    plugin,
    pluginPayload,
    constitutionalLayer,
  });
  const reasonCodes = Array.from(
    new Map(
      [...buildCreateProposalReasonCodes({ draft, walletAddress, plugin, constitutionalLayer }), ...(pluginPayload.reasonCodes ?? [])].map((reason) => [
        reason.reasonCode,
        reason,
      ]),
    ).values(),
  );

  return {
    submissionMode,
    dao: {
      id: selectedDao?.id ?? null,
      address: selectedDao?.address ?? null,
      name: selectedDao?.name ?? null,
      governanceStatus: selectedDao?.governanceStatus ?? null,
      federationTier: selectedDao?.federationTier ?? null,
    },
    chain: {
      network: selectedChain?.network ?? selectedChain?.slug ?? selectedDao?.network ?? null,
      chainId: selectedChain?.chainId ?? null,
      name: selectedChain?.name ?? null,
      role: selectedChain?.roles?.includes('execution') ? 'execution' : selectedChain?.roles?.[0] ?? null,
      governanceStatus: selectedChain?.governanceStatus ?? null,
      federationMember: selectedChain?.federationMember ?? null,
      federationTier: selectedChain?.federationTier ?? null,
      constitutionalStanding: selectedChain?.constitutionalStanding ?? selectedChain?.capabilities?.constitutionalStanding ?? null,
      constitutionalLayer,
    },
    creator: {
      walletAddress: walletAddress ?? null,
    },
    plugin: {
      id: plugin?.id ?? draft.pluginId ?? null,
      address: plugin?.address ?? null,
      interfaceType: plugin?.interfaceType ?? plugin?.pluginType ?? null,
      label: plugin?.name ?? plugin?.interfaceType ?? plugin?.pluginType ?? draft.pluginLabel ?? null,
      createProposalAdapter: {
        family: pluginPayload.adapterFamily,
        status: pluginPayload.adapterStatus,
        expectedBackendAdapter: pluginPayload.expectedBackendAdapter,
        executionIntent: pluginPayload.executionIntent,
      },
    },
    proposal: {
      title: draft.title,
      summary: draft.summary,
      actionType: draft.actionType,
      votingStart: draft.startDate || null,
      votingEnd: draft.endDate || null,
      rationale: draft.rationale || null,
    },
    governanceContext: {
      constitutionalLayer,
      authorityModel: constitutionalLayer?.authorityModel ?? null,
      federationModel: constitutionalLayer?.federationModel ?? null,
      executionModel: constitutionalLayer?.executionModel ?? null,
      observedSources: observedGovernanceContext.observedSources,
      sources: {
        registry: observedGovernanceContext.registry,
        dao: observedGovernanceContext.dao,
        plugin: observedGovernanceContext.plugin,
        treasuryPolicy: observedGovernanceContext.treasuryPolicy,
      },
      source: 'registry-observed-state',
      boundary:
        'Create-proposal requests carry observed Constitutional Layer state only. The frontend does not infer constitutional validity, sanctions or execution authority.',
    },
    adapterPayload: pluginPayload,
    guardrails: {
      frontendBoundary:
        'UI-generated proposal request preview only. Constitutional validity, permissions, sanctions and execution must be evaluated by governance data sources.',
      reasonCodes,
      requiresBackendValidation: true,
      requiresIndexerReconciliation: true,
      noOnchainSubmission: submissionMode !== createProposalSubmissionModes.ONCHAIN,
    },
  };
}

export function isCreateProposalBackendEnabled() {
  return backendCreateProposalEnabled;
}

export function getCreateProposalIntegrationStatus() {
  return {
    submissionMode: backendCreateProposalEnabled ? createProposalSubmissionModes.BACKEND : createProposalSubmissionModes.MOCK_REVIEW,
    backendEnabled: backendCreateProposalEnabled,
    endpoint: createProposalEndpoint,
    reviewRequestsEndpoint: createProposalEndpoint,
    reasonCodes: backendCreateProposalEnabled
      ? []
      : [
          createReason(
            'CREATE_PROPOSAL_BACKEND_NOT_ENABLED',
            'info',
            'frontend submission boundary',
            'Create proposal submission is currently routed to local mock receipts.',
          ),
        ],
    boundary:
      'AxodusAPP forwards or renders create-proposal state only. Constitutional validity, permissions, sanctions, execution and indexing truth must come from governance data sources.',
  };
}

function createReviewRequestsUrl(filters = {}) {
  const url = new URL(createProposalEndpoint, window.location.origin);

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  if (createProposalEndpoint.startsWith('/')) {
    return `${url.pathname}${url.search}`;
  }

  return url.toString();
}

function normalizeCreateProposalReceipt(response, request) {
  const storageMode = response?.storageMode ?? response?.storage?.mode ?? 'unknown';
  const source = response?.source ?? response?.storage?.source ?? 'CreateProposalRequest';

  return {
    id: response?.id ?? response?.receiptId ?? response?.proposalId ?? null,
    proposalDraftId: response?.proposalDraftId ?? null,
    status: response?.status ?? 'backend-submitted',
    submissionMode: createProposalSubmissionModes.BACKEND,
    storageMode,
    source,
    storage: response?.storage ?? {
      mode: storageMode,
      source,
      persisted: storageMode === 'mongo',
      message: 'Create proposal storage metadata was not returned by the backend.',
    },
    submittedAt: response?.submittedAt ?? new Date().toISOString(),
    message: response?.message ?? 'Create proposal request accepted by the Governance API.',
    indexerReconciliation: response?.indexerReconciliation ?? {
      status: 'pending',
      reasonCode: 'INDEXER_STATE_NOT_READY',
      reasonSeverity: 'info',
      message: 'Proposal submission is waiting for indexer reconciliation.',
    },
    request,
    backendResponse: response ?? null,
  };
}

async function readCreateProposalError(response) {
  try {
    const contentType = response.headers?.get?.('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return await response.json();
    }
  } catch {
    return null;
  }

  return null;
}

function createProposalSubmissionError(response, payload) {
  const reasonCode = payload?.reasonCode ?? payload?.error?.reasonCode ?? 'CREATE_PROPOSAL_SUBMISSION_FAILED';
  const reasonSeverity = payload?.reasonSeverity ?? payload?.error?.reasonSeverity ?? 'warning';
  const source = payload?.source ?? payload?.error?.source ?? 'backend submission boundary';
  const message = payload?.message ?? payload?.error?.message ?? `createProposal failed with HTTP ${response.status}`;
  const error = new Error(message);

  error.status = response.status;
  error.reasonCode = reasonCode;
  error.reasonSeverity = reasonSeverity;
  error.source = source;
  error.details = payload?.details ?? payload?.error?.details ?? payload ?? null;

  return error;
}

export function submitCreateProposalMock({ draft, request } = {}) {
  const submittedAt = new Date().toISOString();
  const receiptId = `mock-create-${submittedAt.replace(/[^a-zA-Z0-9]/g, '').slice(0, 14)}`;

  return {
    id: receiptId,
    proposalDraftId: draft?.id ?? null,
    status: 'mock-submitted',
    submissionMode: createProposalSubmissionModes.MOCK_REVIEW,
    storageMode: 'local-only',
    source: 'browser-local-draft',
    storage: {
      mode: 'local-only',
      source: 'browser-local-draft',
      persisted: false,
      message: 'Local mock receipt is stored only in this browser context.',
    },
    submittedAt,
    message: 'Local mock submission accepted for frontend review. No backend write, wallet prompt or on-chain transaction was submitted.',
    indexerReconciliation: {
      status: 'local-only',
      reasonCode: 'INDEXER_STATE_NOT_READY',
      reasonSeverity: 'info',
      message: 'Indexer reconciliation is not available for local mock proposal drafts.',
    },
    request,
  };
}

export async function createGovernanceProposal({ request, signal, fetchImpl = fetch } = {}) {
  if (!backendCreateProposalEnabled) {
    throw new Error('createGovernanceProposal backend submission is disabled. Set VITE_GOVERNANCE_CREATE_PROPOSAL_ENABLED=true to use the API endpoint.');
  }

  const response = await fetchImpl(createProposalEndpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    throw createProposalSubmissionError(response, await readCreateProposalError(response));
  }

  return normalizeCreateProposalReceipt(await response.json(), request);
}

export async function listCreateProposalReviewRequests({ filters = {}, signal, fetchImpl = fetch } = {}) {
  if (!backendCreateProposalEnabled) {
    return {
      items: [],
      count: 0,
      source: 'frontend-disabled',
      endpoint: createProposalEndpoint,
      reasonCodes: [
        createReason(
          'CREATE_PROPOSAL_BACKEND_NOT_ENABLED',
          'info',
          'frontend submission boundary',
          'Create proposal review request listing is disabled while backend submission is disabled.',
        ),
      ],
    };
  }

  const endpoint = createReviewRequestsUrl(filters);
  const response = await fetchImpl(endpoint, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw createProposalSubmissionError(response, await readCreateProposalError(response));
  }

  const payload = await response.json();
  const items = Array.isArray(payload?.items) ? payload.items : [];

  return {
    items,
    count: Number.isFinite(payload?.count) ? payload.count : items.length,
    source: payload?.source ?? 'CreateProposalRequest',
    storageMode: payload?.storageMode ?? items[0]?.storageMode ?? items[0]?.storage?.mode ?? null,
    endpoint,
    reasonCodes: payload?.reasonCodes ?? [],
  };
}

export async function submitCreateProposal({ draft, request, signal, fetchImpl } = {}) {
  if (!backendCreateProposalEnabled) {
    return submitCreateProposalMock({ draft, request });
  }

  return createGovernanceProposal({ request, signal, fetchImpl });
}
